import { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { Upload, File, FileText, Image, Trash2, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAttachmentStore } from '@/store/attachmentStore';
import { useToast } from '@/components/ui/use-toast';
import type { AttachmentEntityType, Attachment } from '@/types/crm';

interface AttachmentSectionProps {
  entityType: AttachmentEntityType;
  entityId: string;
  compact?: boolean;
}

const getFileIcon = (fileType?: string) => {
  if (!fileType) return File;
  if (fileType.startsWith('image/')) return Image;
  if (fileType.includes('pdf') || fileType.includes('document')) return FileText;
  return File;
};

const formatFileSize = (bytes?: number) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export function AttachmentSection({ entityType, entityId, compact = false }: AttachmentSectionProps) {
  const { isLoading, isUploading, fetchAttachments, uploadAttachment, deleteAttachment } = useAttachmentStore();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localAttachments, setLocalAttachments] = useState<Attachment[]>([]);

  useEffect(() => {
    if (entityId) {
      fetchAttachments(entityType, entityId).then(setLocalAttachments);
    }
  }, [entityType, entityId, fetchAttachments]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      for (const file of Array.from(files)) {
        if (file.size > 10 * 1024 * 1024) {
          toast({ title: 'File too large', description: 'Maximum file size is 10MB', variant: 'destructive' });
          continue;
        }
        await uploadAttachment(entityType, entityId, file);
      }
      const updated = await fetchAttachments(entityType, entityId);
      setLocalAttachments(updated);
      toast({ title: 'File uploaded successfully' });
    } catch (error) {
      toast({ title: 'Upload failed', description: String(error), variant: 'destructive' });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (attachment: Attachment) => {
    if (!confirm('Are you sure you want to delete this file?')) return;
    try {
      await deleteAttachment(attachment.id, attachment.storage_path);
      const updated = await fetchAttachments(entityType, entityId);
      setLocalAttachments(updated);
      toast({ title: 'File deleted' });
    } catch (error) {
      toast({ title: 'Delete failed', description: String(error), variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-3">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        className="hidden"
        multiple
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
      />

      <div className="flex items-center justify-between">
        {!compact && (
          <span className="text-sm font-medium">
            Documents ({localAttachments.length})
          </span>
        )}
        <Button
          size="sm"
          variant={compact ? "ghost" : "outline"}
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Upload className="h-4 w-4 mr-1" />
              {compact ? 'Upload' : 'Upload File'}
            </>
          )}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : localAttachments.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-2">No attachments</p>
      ) : (
        <div className="space-y-2">
          {localAttachments.map((attachment) => {
            const FileIcon = getFileIcon(attachment.file_type);
            return (
              <div
                key={attachment.id}
                className="flex items-center justify-between p-2 rounded-md border bg-muted/30 hover:bg-muted/50"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <FileIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{attachment.file_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(attachment.file_size)}
                      {!compact && ` • ${format(new Date(attachment.created_at), 'MMM d, yyyy')}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    asChild
                  >
                    <a href={attachment.file_url} target="_blank" rel="noopener noreferrer" download>
                      <Download className="h-4 w-4" />
                    </a>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleDelete(attachment)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
